// Copyright © 2026 Alex Bruce. All rights reserved.
// Proprietary and confidential. Unauthorized copying, modification,
// or distribution is strictly prohibited.

import React, { useState, useEffect, useCallback } from "react";
import { supabase } from "../lib/supabase";
import OELogo from "./OELogo";
import OEWordmark from "./OEWordmark";
import * as Icon from "./OEIcons";

// ── Palette ──────────────────────────────────────────────────────────────────
const BG      = "#0d1117";
const BG2     = "#161b22";
const BG3     = "#1c2128";
const ACCENT  = "#4fd1c5";
const ACCENT2 = "#f6ad55";
const TEXT    = "#e6edf3";
const TEXT2   = "#8b949e";
const BORDER  = "#30363d";
const FONT    = "'Inter','Avenir Next','Century Gothic',sans-serif";

// ── Helpers ───────────────────────────────────────────────────────────────────
const btn = (filled: boolean, small = false): React.CSSProperties => ({
  display: "inline-flex", alignItems: "center", gap: 6,
  padding: small ? "8px 18px" : "12px 28px",
  borderRadius: 4, border: `1.5px solid ${filled ? ACCENT : BORDER}`,
  background: filled ? ACCENT : "transparent",
  color: filled ? "#0d1117" : TEXT,
  fontFamily: FONT, fontWeight: 700,
  fontSize: small ? 13 : 15,
  cursor: "pointer", letterSpacing: ".02em",
  transition: "opacity .15s, transform .12s",
});

// ── EstimatePanel SVG — Div 08 Openings & Glazing ──────────────────────────────
function EstimatePanel() {
  const W = 580, H = 340;
  const SB = 118;
  const TITLEBAR = 36;

  const tabs = ["Estimate", "Summary", "Breakdown", "Alternates", "Analysis"];

  const sidebarDivs = [
    { label: "Div 06", name: "Wood & Plastics",  amt: "$892,000",   color: "#f6ad55", active: false },
    { label: "Div 07", name: "Thermal",          amt: "$683,360",   color: "#4fd1c5", active: false },
    { label: "Div 08", name: "Openings",         amt: "$1,359,550", color: "#7ab8d4", active: true  },
    { label: "Div 09", name: "Finishes",         amt: "$1,240,000", color: "#b794f4", active: false },
    { label: "Div 10", name: "Specialties",      amt: "$148,000",   color: "#68d391", active: false },
  ];
  const SB_ROWH = (H - TITLEBAR) / sidebarDivs.length;

  const descX  = SB + 10;
  const statCX = SB + 188;
  const statW  = 68;
  const qtyX   = SB + 290;
  const unitX  = SB + 295;
  const ucX    = SB + 356;
  const totX   = SB + 418;
  const pkgX   = SB + 424;
  const pkgW   = 52;

  const DIV_HDR_Y = TITLEBAR;
  const SEC1_Y    = TITLEBAR + 20;
  const COL1_Y    = SEC1_Y + 16;
  const ROWS1_Y   = COL1_Y + 13;
  const ROWH      = 36;
  const SEC2_Y    = ROWS1_Y + 3 * ROWH;
  const COL2_Y    = SEC2_Y + 16;
  const ROWS2_Y   = COL2_Y + 13;

  type StatusType = "hard" | "allow" | "review";
  type Row = {
    desc: string; qty: string; unit: string; uc: string; total: string;
    st: StatusType; pkg: string; red?: boolean;
    bidChip?: string;
    grouped?: boolean;
    groupLeader?: boolean;
    groupCount?: number;
  };

  const SC: Record<StatusType, string> = { hard: "#68d391", allow: "#7ab8d4", review: "#f6ad55" };
  const SL: Record<StatusType, string> = { hard: "Hard Bid", allow: "Allowance", review: "Needs Review" };

  const winRows: Row[] = [
    { desc: "Marvin Ultimate Clad — Main House",  qty: "2,450", unit: "SF", uc: "$285", total: "$698,250",
      st: "hard", pkg: "Gen. Const.", bidChip: "↗  Marvin Windows & Doors  ·  Feb '26", groupLeader: true, groupCount: 2 },
    { desc: "Steel & Glass Curtain Wall — Entry",  qty: "840",   unit: "SF", uc: "$420", total: "$352,800",
      st: "hard", pkg: "Gen. Const.", grouped: true },
    { desc: "Pivot Entry + Side Lite Package",     qty: "2",     unit: "EA", uc: "$32,000", total: "$64,000",
      st: "allow", pkg: "Ext. Improv." },
  ];
  const doorRows: Row[] = [
    { desc: "Interior Door Package — Full House",  qty: "52", unit: "EA", uc: "$3,800", total: "$197,600",
      st: "review", pkg: "Gen. Const." },
    { desc: "Custom Millwork Hardware Allowance",  qty: "1",  unit: "LS", uc: "$46,900", total: "$46,900",
      st: "allow", pkg: "Gen. Const." },
  ];

  const renderColHdr = (y: number) => (
    <g>
      <rect x={SB} y={y} width={W - SB} height={13} fill={BG2 + "cc"}/>
      {([
        ["DESCRIPTION", descX,  "start"],
        ["STATUS",      statCX, "middle"],
        ["QTY",         qtyX,   "end"],
        ["UNIT",        unitX,  "start"],
        ["UNIT COST",   ucX,    "end"],
        ["TOTAL",       totX,   "end"],
        ["PACKAGE",     pkgX,   "start"],
      ] as [string,number,string][]).map(([lbl, x, a]) => (
        <text key={lbl} x={x} y={y + 9} textAnchor={a as any} fill={TEXT2} fontSize={6} fontFamily={FONT} letterSpacing=".08em">{lbl}</text>
      ))}
    </g>
  );

  const renderRow = (r: Row, y: number) => {
    const sc = SC[r.st], sl = SL[r.st];
    const isExt = r.pkg.startsWith("Ext");
    return (
      <g key={r.desc}>
        <line x1={SB} y1={y + ROWH} x2={W} y2={y + ROWH} stroke={BORDER} strokeWidth={0.4} opacity={0.4}/>
        <rect x={SB} y={y} width={3} height={ROWH} fill={r.grouped ? TEXT2 : sc} opacity={r.grouped ? 0.15 : 0.8}/>
        <text x={descX} y={y + 16} fill={TEXT} fontSize={8} fontFamily={FONT}>{r.desc}</text>
        {r.grouped ? (
          <text x={statCX} y={y + 16} textAnchor="middle" fill={TEXT2} fontSize={6.5} fontFamily={FONT} opacity={0.45}>↳ Marvin Windows</text>
        ) : (
          <>
            <rect x={statCX - statW / 2} y={y + 4} width={statW} height={13} rx={3} fill={sc + "28"}/>
            <text x={statCX} y={y + 13} textAnchor="middle" fill={sc} fontSize={6} fontFamily={FONT} fontWeight="700">{sl}</text>
            {r.bidChip && (
              <>
                <rect x={statCX - statW / 2} y={y + 19} width={statW} height={12} rx={2} fill={ACCENT + "18"} stroke={ACCENT + "33"} strokeWidth={0.5}/>
                <text x={statCX} y={y + 27} textAnchor="middle" fill={ACCENT} fontSize={5.5} fontFamily={FONT} fontWeight="600">{r.bidChip}</text>
              </>
            )}
            {r.groupLeader && (
              <text x={statCX - statW / 2 + 3} y={y + 34} fill={TEXT2} fontSize={5} fontFamily={FONT} opacity={0.4}>⊕ {r.groupCount} grouped</text>
            )}
          </>
        )}
        <text x={qtyX} y={y + 16} textAnchor="end" fill={TEXT2} fontSize={7.5} fontFamily={FONT}>{r.qty}</text>
        <text x={unitX + 2} y={y + 16} fill={TEXT2} fontSize={7.5} fontFamily={FONT}>{r.unit}</text>
        <text x={ucX} y={y + 16} textAnchor="end" fill={TEXT2} fontSize={7.5} fontFamily={FONT}>{r.uc}</text>
        <text x={totX} y={y + 16} textAnchor="end" fill={TEXT} fontSize={8} fontFamily={FONT} fontWeight="600">{r.total}</text>
        <rect x={pkgX} y={y + 4} width={pkgW} height={13} rx={2} fill={isExt ? ACCENT2 + "22" : ACCENT + "18"}/>
        <text x={pkgX + pkgW / 2} y={y + 13} textAnchor="middle" fill={isExt ? ACCENT2 : ACCENT} fontSize={5.5} fontFamily={FONT} fontWeight="700">{r.pkg}</text>
      </g>
    );
  };

  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ display: "block", borderRadius: 8 }}>
      <defs>
        <linearGradient id="ep_glow" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={ACCENT} stopOpacity=".07"/>
          <stop offset="100%" stopColor={BG3} stopOpacity="0"/>
        </linearGradient>
        <linearGradient id="ep_fade" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={BG} stopOpacity="0"/>
          <stop offset="100%" stopColor={BG} stopOpacity=".96"/>
        </linearGradient>
        <clipPath id="ep_clip"><rect width={W} height={H} rx={8}/></clipPath>
      </defs>

      <rect width={W} height={H} rx={8} fill={BG3} stroke={BORDER} strokeWidth={1.5}/>
      <rect width={W} height={H} rx={8} fill="url(#ep_glow)"/>

      {/* Title bar */}
      <rect width={W} height={TITLEBAR} rx={8} fill={BG2}/>
      <rect y={TITLEBAR - 8} width={W} height={8} fill={BG2}/>
      <circle cx={16} cy={18} r={5} fill="#ff5f57" opacity=".85"/>
      <circle cx={30} cy={18} r={5} fill="#febc2e" opacity=".85"/>
      <circle cx={44} cy={18} r={5} fill="#28c840" opacity=".85"/>

      {tabs.map((t, i) => {
        const active = i === 0;
        const tabW = 72, startX = 60 + i * (tabW + 2);
        return (
          <g key={t}>
            <text x={startX + tabW / 2} y={23} textAnchor="middle"
              fill={active ? ACCENT : TEXT2} fontSize={8} fontFamily={FONT} fontWeight={active ? "700" : "400"}
              letterSpacing=".04em">{t.toUpperCase()}</text>
            {active && <line x1={startX + 6} y1={32} x2={startX + tabW - 6} y2={32} stroke={ACCENT} strokeWidth={2} strokeLinecap="round"/>}
          </g>
        );
      })}

      <rect x={W - 104} y={8} width={96} height={20} rx={3} fill={ACCENT + "18"} stroke={ACCENT + "44"} strokeWidth={1}/>
      <text x={W - 56} y={22} textAnchor="middle" fill={ACCENT} fontSize={10} fontFamily={FONT} fontWeight="bold">$8,540,000</text>

      {/* Sidebar */}
      <rect x={0} y={TITLEBAR} width={SB} height={H - TITLEBAR} fill={BG2} stroke={BORDER} strokeWidth={0.5}/>
      {sidebarDivs.map((d, i) => {
        const ry = TITLEBAR + Math.round(i * SB_ROWH);
        const rh = Math.round(SB_ROWH);
        return (
          <g key={d.label}>
            {d.active && <rect x={0} y={ry} width={SB} height={rh} fill={ACCENT + "12"}/>}
            <rect x={4} y={ry + 7} width={3} height={rh - 14} rx={1.5} fill={d.color} opacity={0.9}/>
            <text x={13} y={ry + 18} fill={d.active ? "#7ab8d4" : TEXT2} fontSize={7} fontFamily={FONT} fontWeight={d.active ? "700" : "400"} letterSpacing=".04em">{d.label}</text>
            <text x={13} y={ry + 30} fill={d.active ? TEXT : TEXT2} fontSize={8} fontFamily={FONT} fontWeight={d.active ? "600" : "400"}>{d.name}</text>
            <text x={13} y={ry + 43} fill={d.active ? "#7ab8d4" : TEXT2} fontSize={8.5} fontFamily={FONT} fontWeight={d.active ? "700" : "400"}>{d.amt}</text>
            {d.active && <rect x={SB - 3} y={ry + 7} width={3} height={rh - 14} rx={1.5} fill={ACCENT}/>}
            <line x1={0} y1={ry + rh} x2={SB} y2={ry + rh} stroke={BORDER} strokeWidth={0.4} opacity={0.3}/>
          </g>
        );
      })}

      {/* Content */}
      <rect x={SB} y={TITLEBAR} width={W - SB} height={H - TITLEBAR} fill={BG}/>

      <rect x={SB} y={DIV_HDR_Y} width={W - SB} height={20} fill={BG2 + "99"}/>
      <text x={SB + 10} y={DIV_HDR_Y + 9} fill={TEXT2} fontSize={6.5} fontFamily={FONT} letterSpacing=".1em">DIV 08</text>
      <text x={SB + 38} y={DIV_HDR_Y + 9} fill={TEXT} fontSize={6.5} fontFamily={FONT} fontWeight="600">Openings &amp; Glazing</text>
      <text x={W - 148} y={DIV_HDR_Y + 9} fill={TEXT2} fontSize={6} fontFamily={FONT}>GEN. CONST.</text>
      <text x={W - 148} y={DIV_HDR_Y + 18} fill={ACCENT} fontSize={7.5} fontFamily={FONT} fontWeight="700">$1,095,550</text>
      <text x={W - 92} y={DIV_HDR_Y + 9} fill={TEXT2} fontSize={6} fontFamily={FONT}>EXT. IMPROV.</text>
      <text x={W - 92} y={DIV_HDR_Y + 18} fill={ACCENT2} fontSize={7.5} fontFamily={FONT} fontWeight="700">$264,000</text>
      <text x={W - 10} y={DIV_HDR_Y + 18} textAnchor="end" fill={TEXT} fontSize={8.5} fontFamily={FONT} fontWeight="700">$1,359,550</text>

      <rect x={SB} y={SEC1_Y} width={W - SB} height={16} fill={BG3}/>
      <line x1={SB} y1={SEC1_Y} x2={W} y2={SEC1_Y} stroke={BORDER} strokeWidth={0.5}/>
      <rect x={SB + 6} y={SEC1_Y + 4} width={2.5} height={8} rx={1.25} fill="#7ab8d4" opacity={0.8}/>
      <text x={SB + 14} y={SEC1_Y + 12} fill={TEXT} fontSize={8} fontFamily={FONT} fontWeight="700" letterSpacing=".04em">WINDOWS &amp; GLAZING</text>
      <text x={SB + 120} y={SEC1_Y + 12} fill={TEXT2} fontSize={6.5} fontFamily={FONT} opacity={0.6}>08-500</text>
      {renderColHdr(COL1_Y)}
      {winRows.map((r, i) => renderRow(r, ROWS1_Y + i * ROWH))}

      <rect x={SB} y={SEC2_Y} width={W - SB} height={16} fill={BG3}/>
      <line x1={SB} y1={SEC2_Y} x2={W} y2={SEC2_Y} stroke={BORDER} strokeWidth={0.5}/>
      <rect x={SB + 6} y={SEC2_Y + 4} width={2.5} height={8} rx={1.25} fill={ACCENT2} opacity={0.8}/>
      <text x={SB + 14} y={SEC2_Y + 12} fill={TEXT} fontSize={8} fontFamily={FONT} fontWeight="700" letterSpacing=".04em">DOORS &amp; HARDWARE</text>
      <text x={SB + 102} y={SEC2_Y + 12} fill={TEXT2} fontSize={6.5} fontFamily={FONT} opacity={0.6}>08-100</text>
      {renderColHdr(COL2_Y)}
      {doorRows.map((r, i) => renderRow(r, ROWS2_Y + i * ROWH))}

      <rect x={SB} y={H - 72} width={W - SB} height={72} fill="url(#ep_fade)" clipPath="url(#ep_clip)"/>

      <rect x={SB} y={H - 28} width={W - SB} height={28} fill={BG2}/>
      <line x1={SB} y1={H - 28} x2={W} y2={H - 28} stroke={BORDER} strokeWidth={0.5}/>
      <text x={SB + 10} y={H - 11} fill={TEXT2} fontSize={8} fontFamily={FONT} letterSpacing=".06em">DIV 08 SUBTOTAL</text>
      <text x={W - 12} y={H - 11} textAnchor="end" fill={TEXT} fontSize={10} fontFamily={FONT} fontWeight="700">$1,359,550</text>

      <rect width={W} height={H} rx={8} fill="none" stroke={ACCENT} strokeWidth={1} opacity={0.2}/>
    </svg>
  );
}

// ── VEPanel SVG — Value Engineering Analysis ───────────────────────────────────
function VEPanel() {
  const W = 340, H = 192;
  const maxBarW = 195;

  type VEEntry = { label: string; val: string; pct: number; color: string };
  const adds: VEEntry[] = [
    { label: "Upgraded MEP Package",       val: "+$186,000", pct: 186 / 334, color: "#68d391" },
    { label: "Enhanced Pool & Spa Equip.", val: "+$220,000", pct: 220 / 334, color: "#68d391" },
  ];
  const deds: VEEntry[] = [
    { label: "Standard vs. Custom Glazing",  val: "-$334,000", pct: 334 / 334, color: "#fc8181" },
    { label: "Plaster vs. Drywall Finishes", val: "-$280,000", pct: 280 / 334, color: "#fc8181" },
  ];

  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ display: "block", borderRadius: 8 }}>
      <defs>
        <linearGradient id="ve_glow" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#68d391" stopOpacity=".05"/>
          <stop offset="100%" stopColor={BG3} stopOpacity="0"/>
        </linearGradient>
      </defs>

      <rect width={W} height={H} rx={8} fill={BG3} stroke={BORDER} strokeWidth={1.5}/>
      <rect width={W} height={H} rx={8} fill="url(#ve_glow)"/>

      {/* Header */}
      <rect width={W} height={32} rx={8} fill={BG2}/>
      <rect y={24} width={W} height={8} fill={BG2}/>
      <text x={12} y={20} fill={TEXT} fontSize={10} fontFamily={FONT} fontWeight="700">Value Engineering</text>
      <rect x={W - 110} y={7} width={100} height={18} rx={3} fill={ACCENT + "18"} stroke={ACCENT + "44"} strokeWidth={1}/>
      <text x={W - 60} y={20} textAnchor="middle" fill={ACCENT} fontSize={8.5} fontFamily={FONT} fontWeight="600">Base: $8,540,000</text>

      {/* ADD ALTERNATES */}
      <text x={12} y={46} fill="#68d391" fontSize={7.5} fontFamily={FONT} letterSpacing=".1em" fontWeight="700">ADD ALTERNATES</text>
      {adds.map((a, i) => {
        const barW = Math.round(a.pct * maxBarW);
        const ry = 52 + i * 28;
        return (
          <g key={a.label}>
            <text x={12} y={ry + 11} fill={TEXT2} fontSize={8} fontFamily={FONT}>{a.label}</text>
            <rect x={12} y={ry + 15} width={barW} height={7} rx={3.5} fill="#68d391" opacity={0.65}/>
            <text x={12 + barW + 6} y={ry + 22} fill="#68d391" fontSize={8} fontFamily={FONT} fontWeight="600">{a.val}</text>
          </g>
        );
      })}

      {/* DEDUCTIVE ALTERNATES */}
      <text x={12} y={118} fill="#fc8181" fontSize={7.5} fontFamily={FONT} letterSpacing=".1em" fontWeight="700">DEDUCTIVE ALTERNATES</text>
      {deds.map((a, i) => {
        const barW = Math.round(a.pct * maxBarW);
        const ry = 124 + i * 28;
        return (
          <g key={a.label}>
            <text x={12} y={ry + 11} fill={TEXT2} fontSize={8} fontFamily={FONT}>{a.label}</text>
            <rect x={12} y={ry + 15} width={barW} height={7} rx={3.5} fill="#fc8181" opacity={0.65}/>
            <text x={12 + barW + 6} y={ry + 22} fill="#fc8181" fontSize={8} fontFamily={FONT} fontWeight="600">{a.val}</text>
          </g>
        );
      })}

      {/* Footer */}
      <rect x={0} y={H - 26} width={W} height={26} rx={8} fill={BG2}/>
      <rect x={0} y={H - 26} width={W} height={8} fill={BG2}/>
      <line x1={0} y1={H - 26} x2={W} y2={H - 26} stroke={BORDER} strokeWidth={0.5}/>
      <text x={12} y={H - 10} fill={TEXT2} fontSize={8} fontFamily={FONT}>Net Deductive Available</text>
      <text x={W - 12} y={H - 10} textAnchor="end" fill="#fc8181" fontSize={9.5} fontFamily={FONT} fontWeight="700">-$208,000</text>

      <rect width={W} height={H} rx={8} fill="none" stroke={BORDER} strokeWidth={1} opacity={0.4}/>
    </svg>
  );
}

// ── BidTimelinePanel SVG — Bid tracking & leveling ─────────────────────────────
function BidTimelinePanel() {
  const W = 340, H = 192;

  // Plot geometry
  const plotX1 = 46, plotX2 = W - 10;
  const plotY1 = 44, plotY2 = 154;
  const plotW  = plotX2 - plotX1;  // 284
  const plotH  = plotY2 - plotY1;  // 110

  const yMin = 100000, yMax = 500000;
  const ys = (v: number) => plotY1 + plotH * (1 - (v - yMin) / (yMax - yMin));
  const xs = (m: number) => plotX1 + (m / 9) * plotW;

  // Scope colors & names
  const C = ["#f6ad55", "#68d391", "#4fd1c5", "#b794f4", "#f687b3"];
  const scopeNames = ["Sitework", "Framing", "Envelope", "MEP", "Finishes"];

  // Bids: scope index, month (0=May'25 … 9=Feb'26), amount, superseded
  type Bid = { s: number; m: number; v: number; sup: boolean };
  const bids: Bid[] = [
    { s: 0, m: 1,   v: 285000, sup: true  },
    { s: 0, m: 4,   v: 262000, sup: true  },
    { s: 0, m: 7.5, v: 248000, sup: false },
    { s: 1, m: 1,   v: 435000, sup: true  },
    { s: 1, m: 3,   v: 418000, sup: true  },
    { s: 1, m: 6,   v: 395000, sup: false },
    { s: 2, m: 2.5, v: 178000, sup: true  },
    { s: 2, m: 6,   v: 165000, sup: false },
    { s: 3, m: 4,   v: 320000, sup: true  },
    { s: 3, m: 7,   v: 295000, sup: false },
    { s: 4, m: 5,   v: 210000, sup: false },
    { s: 4, m: 8,   v: 195000, sup: false },
  ];

  // SD / DD milestone lines
  const milestones = [
    { m: 2, label: "SD Budget"   },
    { m: 6, label: "DD Estimate" },
  ];

  // Y-axis gridlines
  const yGrids: [number, string][] = [
    [450000, "$450k"], [350000, "$350k"], [250000, "$250k"], [150000, "$150k"],
  ];

  // X-axis labels
  const xLabels: [number, string][] = [
    [0, "May '25"], [3, "Aug '25"], [6, "Nov '25"], [9, "Feb '26"],
  ];

  // Trend polylines per scope (time-ordered)
  const trendLines = scopeNames.map((_, si) =>
    bids.filter(b => b.s === si).sort((a, b) => a.m - b.m)
      .map(b => `${xs(b.m).toFixed(1)},${ys(b.v).toFixed(1)}`).join(" ")
  );

  // Scope pills in footer
  const pillW = 55, pillH = 12, pillGap = 5;
  const pillsTotal = scopeNames.length * pillW + (scopeNames.length - 1) * pillGap;
  const pillX0 = (W - pillsTotal) / 2;

  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ display: "block", borderRadius: 8 }}>
      <defs>
        <linearGradient id="bt_glow" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={ACCENT} stopOpacity=".05"/>
          <stop offset="100%" stopColor={BG3} stopOpacity="0"/>
        </linearGradient>
      </defs>

      <rect width={W} height={H} rx={8} fill={BG3} stroke={BORDER} strokeWidth={1.5}/>
      <rect width={W} height={H} rx={8} fill="url(#bt_glow)"/>

      {/* ── Header ── */}
      <rect width={W} height={32} rx={8} fill={BG2}/>
      <rect y={24} width={W} height={8} fill={BG2}/>
      <text x={12} y={20} fill={TEXT} fontSize={10} fontFamily={FONT} fontWeight="700">Bid Timeline</text>
      <rect x={W - 116} y={7} width={106} height={18} rx={3} fill={ACCENT + "18"} stroke={ACCENT + "44"} strokeWidth={1}/>
      <text x={W - 63} y={20} textAnchor="middle" fill={ACCENT} fontSize={8} fontFamily={FONT} fontWeight="600">5 scopes · 12 bids</text>

      {/* ── Y gridlines + labels ── */}
      {yGrids.map(([v, lbl]) => {
        const y = ys(v);
        return (
          <g key={v}>
            <line x1={plotX1} y1={y} x2={plotX2} y2={y} stroke={BORDER} strokeWidth={0.5} strokeDasharray="3 3" opacity={0.5}/>
            <text x={plotX1 - 4} y={y + 3} textAnchor="end" fill={TEXT2} fontSize={6} fontFamily={FONT} opacity={0.6}>{lbl}</text>
          </g>
        );
      })}

      {/* ── Axes ── */}
      <line x1={plotX1} y1={plotY1 - 2} x2={plotX1} y2={plotY2} stroke={BORDER} strokeWidth={0.5} opacity={0.7}/>
      <line x1={plotX1} y1={plotY2}     x2={plotX2} y2={plotY2} stroke={BORDER} strokeWidth={0.5} opacity={0.7}/>

      {/* ── X labels ── */}
      {xLabels.map(([m, lbl]) => (
        <text key={m} x={xs(m)} y={plotY2 + 9} textAnchor="middle" fill={TEXT2} fontSize={6} fontFamily={FONT} opacity={0.55}>{lbl}</text>
      ))}

      {/* ── Milestone verticals ── */}
      {milestones.map(ml => {
        const x = xs(ml.m);
        const lw = ml.label.length * 4 + 8;
        return (
          <g key={ml.label}>
            <line x1={x} y1={plotY1 - 2} x2={x} y2={plotY2} stroke="#e2c96e" strokeWidth={1} strokeDasharray="3 2.5" opacity={0.55}/>
            <rect x={x - lw / 2} y={33} width={lw} height={10} rx={2} fill="#e2c96e18" stroke="#e2c96e44" strokeWidth={0.5}/>
            <text x={x} y={41} textAnchor="middle" fill="#e2c96e" fontSize={5.5} fontFamily={FONT} fontWeight="600">{ml.label}</text>
          </g>
        );
      })}

      {/* ── Trend polylines (behind dots) ── */}
      {trendLines.map((pts, si) =>
        pts ? <polyline key={si} points={pts} fill="none" stroke={C[si]} strokeWidth={0.9} opacity={0.22}/> : null
      )}

      {/* ── Bid dots ── */}
      {bids.map((b, i) => {
        const cx = xs(b.m), cy = ys(b.v), c = C[b.s];
        return b.sup ? (
          <g key={i}>
            <circle cx={cx} cy={cy} r={3.5} fill={c} opacity={0.1}/>
            <circle cx={cx} cy={cy} r={3.5} fill="none" stroke={c} strokeWidth={0.75} strokeDasharray="2 1.5" opacity={0.38}/>
          </g>
        ) : (
          <circle key={i} cx={cx} cy={cy} r={4} fill={c} opacity={0.9}/>
        );
      })}

      {/* ── Footer: scope pills ── */}
      <rect x={0} y={H - 26} width={W} height={26} rx={8} fill={BG2}/>
      <rect x={0} y={H - 26} width={W} height={8} fill={BG2}/>
      <line x1={0} y1={H - 26} x2={W} y2={H - 26} stroke={BORDER} strokeWidth={0.4} opacity={0.5}/>
      {scopeNames.map((name, i) => {
        const px = pillX0 + i * (pillW + pillGap);
        const py = H - 20;
        return (
          <g key={name}>
            <rect x={px} y={py} width={pillW} height={pillH} rx={pillH / 2} fill={C[i] + "1c"} stroke={C[i] + "55"} strokeWidth={0.5}/>
            <circle cx={px + 7} cy={py + pillH / 2} r={2.5} fill={C[i]}/>
            <text x={px + 13} y={py + 8.5} fill={TEXT2} fontSize={6.5} fontFamily={FONT}>{name}</text>
          </g>
        );
      })}

      <rect width={W} height={H} rx={8} fill="none" stroke={BORDER} strokeWidth={1} opacity={0.4}/>
    </svg>
  );
}

// ── Feature Card ──────────────────────────────────────────────────────────────
function FeatureCard({ icon, title, desc, accentColor = ACCENT }: { icon: React.ReactNode; title: string; desc: string; accentColor?: string }) {
  return (
    <div style={{
      background: BG3, border: `1px solid ${BORDER}`,
      borderTop: `2px solid ${accentColor}`, borderRadius: 8,
      padding: "28px 24px", display: "flex", flexDirection: "column", gap: 14,
    }}>
      <div style={{
        width: 40, height: 40, borderRadius: 8,
        background: accentColor + "15", border: `1px solid ${accentColor}30`,
        display: "flex", alignItems: "center", justifyContent: "center",
        flexShrink: 0,
      }}>{icon}</div>
      <div style={{ fontWeight: 700, fontSize: 15, color: TEXT, fontFamily: FONT, letterSpacing: "-.01em" }}>{title}</div>
      <div style={{ fontSize: 13, color: TEXT2, fontFamily: FONT, lineHeight: 1.65 }}>{desc}</div>
    </div>
  );
}

// ── Step ──────────────────────────────────────────────────────────────────────
function Step({ n, title, desc, last, stacked }: { n: number; title: string; desc: string; last?: boolean; stacked?: boolean }) {
  return (
    <div style={{ flex: stacked ? "none" : 1, display: "flex", gap: 16, alignItems: "flex-start", position: "relative" }}>
      <div style={{
        flexShrink: 0, width: 36, height: 36, borderRadius: "50%",
        background: ACCENT + "18", border: `2px solid ${ACCENT}`,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontFamily: FONT, fontWeight: 800, fontSize: 15, color: ACCENT,
      }}>{n}</div>
      {!last && !stacked && (
        <div style={{
          position: "absolute", left: 36, top: 17, width: "calc(100% - 36px + 16px)",
          height: 1, borderTop: `1.5px dashed ${BORDER}`, pointerEvents: "none",
        }}/>
      )}
      <div style={{ paddingTop: 4 }}>
        <div style={{ fontWeight: 700, fontSize: 15, color: TEXT, fontFamily: FONT, marginBottom: 6 }}>{title}</div>
        <div style={{ fontSize: 13, color: TEXT2, fontFamily: FONT, lineHeight: 1.65 }}>{desc}</div>
      </div>
    </div>
  );
}

// ── Main LandingPage Component ─────────────────────────────────────────────────
interface LandingPageProps {
  onLogin: () => void;
  onGetStarted: () => void;
}

// ── Contact + Footer ────────────────────────────────────────────────────────
function ContactFooter({ hPad, isMobile }: { hPad: string; isMobile: boolean }) {
  const [name,    setName]    = useState("");
  const [email,   setEmail]   = useState("");
  const [subject, setSubject] = useState("General Inquiry");
  const [message, setMessage] = useState("");
  const [state,   setState]   = useState<"idle"|"sending"|"done"|"error">("idle");

  const SUBJECTS = [
    "General Inquiry",
    "Demo Request",
    "Enterprise / Team Pricing",
    "Bug Report",
    "Feature Request",
    "Other",
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !message.trim()) return;
    setState("sending");
    try {
      const res = await fetch("https://formspree.io/f/xdkobpva", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ name: name.trim(), email: email.trim(), subject, message: message.trim() }),
      });
      if (!res.ok) throw new Error();
      setState("done");
    } catch {
      setState("error");
    }
  };

  const inputStyle: React.CSSProperties = {
    width: "100%", background: BG3, border: `1px solid ${BORDER}`, borderRadius: 6,
    padding: "10px 14px", fontSize: 13, color: TEXT, fontFamily: FONT, outline: "none",
    boxSizing: "border-box",
  };
  const labelStyle: React.CSSProperties = {
    display: "block", fontSize: 10, fontWeight: 700, color: TEXT2,
    letterSpacing: ".08em", textTransform: "uppercase", marginBottom: 6,
  };

  return (
    <>
      {/* ── Contact section ── */}
      <section id="contact" style={{
        background: BG2, borderTop: `1px solid ${BORDER}`,
        padding: `72px ${hPad}`,
      }}>
        <div style={{ maxWidth: 1080, margin: "0 auto", display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
          gap: isMobile ? 40 : 80, alignItems: "start" }}>

          {/* ── Left: info ── */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".14em",
              textTransform: "uppercase", color: ACCENT, marginBottom: 14 }}>
              Contact Us
            </div>
            <h2 style={{ margin: "0 0 16px", fontSize: isMobile ? 28 : 34,
              fontWeight: 800, color: TEXT, letterSpacing: "-.02em", lineHeight: 1.2 }}>
              Get in touch
            </h2>
            <p style={{ margin: "0 0 32px", fontSize: 15, color: TEXT2, lineHeight: 1.75, maxWidth: 380 }}>
              Questions about pricing, enterprise access, or just want to see a live demo?
              We read every message and reply within one business day.
            </p>

            <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
              {[
                { icon: <Icon.Envelope size={16}/>, label: "Email", value: "info@open-estimate.com",
                  href: "mailto:info@open-estimate.com" },
                { icon: <Icon.MessageSquare size={16}/>, label: "Demo", value: "Request a live walkthrough",
                  href: undefined, action: true },
              ].map(({ icon, label, value, href }) => (
                <div key={label} style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
                  <div style={{ width: 38, height: 38, borderRadius: 8, background: BG3,
                    border: `1px solid ${BORDER}`, display: "flex", alignItems: "center",
                    justifyContent: "center", flexShrink: 0 }}>
                    {icon}
                  </div>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: TEXT2,
                      letterSpacing: ".06em", textTransform: "uppercase", marginBottom: 2 }}>{label}</div>
                    {href ? (
                      <a href={href} style={{ fontSize: 14, color: ACCENT,
                        textDecoration: "none", fontWeight: 500 }}>{value}</a>
                    ) : (
                      <span style={{ fontSize: 14, color: TEXT2 }}>{value}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ── Right: form ── */}
          <div style={{ background: BG3, border: `1px solid ${BORDER}`,
            borderRadius: 10, padding: isMobile ? "24px 20px" : "32px 28px" }}>

            {state === "done" ? (
              <div style={{ textAlign: "center", padding: "24px 0" }}>
                <div style={{ marginBottom: 14, display: "flex", justifyContent: "center" }}><Icon.CheckCircle size={20}/></div>
                <div style={{ fontSize: 20, fontWeight: 800, color: TEXT, marginBottom: 10 }}>Message sent!</div>
                <p style={{ fontSize: 13, color: TEXT2, lineHeight: 1.7, margin: "0 0 24px" }}>
                  Thanks for reaching out. We'll get back to you within one business day.
                </p>
                <button onClick={() => { setState("idle"); setName(""); setEmail(""); setMessage(""); setSubject("General Inquiry"); }}
                  style={{ ...btn(false, true), width: "100%", justifyContent: "center" }}>
                  Send another message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} noValidate>
                <div style={{ marginBottom: 18 }}>
                  <label style={labelStyle}>Your name <span style={{ color: ACCENT }}>*</span></label>
                  <input value={name} onChange={e => setName(e.target.value)} placeholder="Jane Smith"
                    style={inputStyle} required
                    onFocus={e => e.target.style.borderColor = ACCENT}
                    onBlur={e => e.target.style.borderColor = BORDER} />
                </div>

                <div style={{ marginBottom: 18 }}>
                  <label style={labelStyle}>Email address <span style={{ color: ACCENT }}>*</span></label>
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                    placeholder="jane@company.com" style={inputStyle} required
                    onFocus={e => e.target.style.borderColor = ACCENT}
                    onBlur={e => e.target.style.borderColor = BORDER} />
                </div>

                <div style={{ marginBottom: 18 }}>
                  <label style={labelStyle}>Subject</label>
                  <select value={subject} onChange={e => setSubject(e.target.value)}
                    style={{ ...inputStyle, cursor: "pointer" }}>
                    {SUBJECTS.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>

                <div style={{ marginBottom: 22 }}>
                  <label style={labelStyle}>Message <span style={{ color: ACCENT }}>*</span></label>
                  <textarea value={message} onChange={e => setMessage(e.target.value)}
                    placeholder="Tell us what you're working on or what you'd like to know…"
                    rows={5} required
                    style={{ ...inputStyle, resize: "vertical" }}
                    onFocus={e => e.target.style.borderColor = ACCENT}
                    onBlur={e => e.target.style.borderColor = BORDER} />
                </div>

                {state === "error" && (
                  <div style={{ marginBottom: 14, padding: "10px 14px", background: "#c0392b18",
                    border: "1px solid #c0392b44", borderRadius: 6, fontSize: 12, color: "#e74c3c" }}>
                    Something went wrong — please try again or email us directly at info@open-estimate.com
                  </div>
                )}

                <button type="submit" disabled={state === "sending" || !name.trim() || !email.trim() || !message.trim()}
                  style={{ ...btn(true), width: "100%", justifyContent: "center",
                    opacity: (state === "sending" || !name.trim() || !email.trim() || !message.trim()) ? 0.5 : 1,
                    transition: "opacity .15s" }}>
                  {state === "sending" ? "Sending…" : "Send Message →"}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* ── Footer bar ── */}
      <footer style={{
        background: BG, borderTop: `1px solid ${BORDER}`,
        padding: `20px ${hPad}`,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        flexWrap: "wrap", gap: 12,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <OELogo height={20} color={ACCENT}/>
          <span style={{ fontSize: 12, color: TEXT2 }}>
            © 2026 OpenEstimate · All rights reserved
          </span>
        </div>
        <div style={{ display: "flex", gap: 20, alignItems: "center" }}>
          <a href="mailto:info@open-estimate.com"
            style={{ fontSize: 12, color: TEXT2, textDecoration: "none", opacity: .7 }}>
            info@open-estimate.com
          </a>
          {[
            { label: "Security", href: "#/security" },
            { label: "Privacy Policy", href: "#/privacy" },
            { label: "Terms of Use", href: "#/terms" },
          ].map(({ label, href }) => (
            <a key={label} href={href}
              style={{ fontSize: 12, color: TEXT2, textDecoration: "none", opacity: .7, fontFamily: FONT }}>
              {label}
            </a>
          ))}
        </div>
      </footer>
    </>
  );
}

export default function LandingPage({ onLogin, onGetStarted }: LandingPageProps) {
  const [scrolled, setScrolled] = useState(false);
  const [winW, setWinW] = useState(() => typeof window !== "undefined" ? window.innerWidth : 1440);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onResize = () => setWinW(window.innerWidth);
    window.addEventListener("resize", onResize, { passive: true });
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // ── Request-access modal state ──────────────────────────────────────────────
  const [showModal, setShowModal]   = useState(false);
  const [reqName,   setReqName]     = useState("");
  const [reqEmail,  setReqEmail]    = useState("");
  const [reqMsg,    setReqMsg]      = useState("");
  const [reqState,  setReqState]    = useState<"idle"|"sending"|"done"|"error">("idle");

  const openModal = useCallback(() => {
    setReqName(""); setReqEmail(""); setReqMsg(""); setReqState("idle");
    setShowModal(true);
  }, []);

  const submitRequest = useCallback(async () => {
    if (!reqName.trim() || !reqEmail.trim()) return;
    setReqState("sending");
    try {
      const res = await fetch("/api/access-request", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: reqName.trim(), email: reqEmail.trim(), message: reqMsg.trim() }),
      });
      if (!res.ok) throw new Error("Request failed");
      setReqState("done");
    } catch {
      setReqState("error");
    }
  }, [reqName, reqEmail, reqMsg]);

  const isMobile  = winW < 640;
  const isTablet  = winW < 1024;
  // Shared horizontal padding that shrinks on narrow viewports
  const hPad = isMobile ? "20px" : isTablet ? "32px" : "48px";

  const features = [
    {
      accentColor: "#68d391",
      icon: (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <circle cx="10" cy="10" r="7.5" stroke="#68d391" strokeWidth="1.5" fill="none"/>
          <path d="M7 10l2 2 4-4" stroke="#68d391" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      title: "Cost Confidence Scoring",
      desc: "Every line item is tagged: Hard Bid, Allowance, or Needs Review — with color-coded rows and proposal-level confidence rollups. You always know exactly where your exposure is.",
    },
    {
      accentColor: ACCENT2,
      icon: (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <rect x="2" y="5" width="16" height="3" rx="1.5" fill={ACCENT2}/>
          <rect x="2" y="10" width="10" height="3" rx="1.5" fill={ACCENT2} opacity=".6"/>
          <path d="M14 11.5l3 3-3 3" stroke={ACCENT2} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" opacity=".8"/>
        </svg>
      ),
      title: "Value Engineering Architecture",
      desc: "Add and deductive alternates are first-class citizens — not afterthoughts. Isolate scope options, see net financial impact across the whole project, and present VE scenarios with clarity.",
    },
    {
      accentColor: ACCENT,
      icon: (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <rect x="3" y="2" width="14" height="16" rx="2" stroke={ACCENT} strokeWidth="1.5" fill="none"/>
          <line x1="6.5" y1="7" x2="13.5" y2="7" stroke={ACCENT} strokeWidth="1.2" strokeLinecap="round"/>
          <line x1="6.5" y1="10" x2="11" y2="10" stroke={ACCENT} strokeWidth="1.2" strokeLinecap="round" opacity=".6"/>
          <rect x="6" y="13" width="8" height="2.5" rx="1.25" fill={ACCENT} opacity=".25"/>
        </svg>
      ),
      title: "Client-Ready Reports, Always",
      desc: "Your estimate is always presentation-ready. Print views, CSV exports, and executive summaries are built for owner meetings — not just internal use. Branded, visual, and current.",
    },
    {
      accentColor: "#b794f4",
      icon: (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path d="M10 3 L10 9 M7 6l3-3 3 3" stroke="#b794f4" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          <rect x="3" y="10" width="14" height="8" rx="2" stroke="#b794f4" strokeWidth="1.5" fill="none"/>
          <line x1="7" y1="14" x2="13" y2="14" stroke="#b794f4" strokeWidth="1.2" strokeLinecap="round" opacity=".6"/>
        </svg>
      ),
      title: "AI-Powered Estimate Import",
      desc: "Drop in a contractor proposal, scope sheet, or bid tab and let AI parse it into a structured CSI estimate skeleton. Skip hours of manual re-entry and go straight to reviewing numbers.",
    },
    {
      accentColor: "#7ab8d4",
      icon: (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <circle cx="10" cy="10" r="7.5" stroke="#7ab8d4" strokeWidth="1.5" fill="none"/>
          <circle cx="10" cy="10" r="3" fill="#7ab8d4" opacity=".3"/>
          <line x1="10" y1="2.5" x2="10" y2="5" stroke="#7ab8d4" strokeWidth="1.5" strokeLinecap="round"/>
          <line x1="10" y1="15" x2="10" y2="17.5" stroke="#7ab8d4" strokeWidth="1.5" strokeLinecap="round"/>
          <line x1="2.5" y1="10" x2="5" y2="10" stroke="#7ab8d4" strokeWidth="1.5" strokeLinecap="round"/>
          <line x1="15" y1="10" x2="17.5" y2="10" stroke="#7ab8d4" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
      ),
      title: "Design-Phase Flexibility",
      desc: "Works across the full design arc — schematic, design development, and construction documents. Stipulated sum, cost-plus, and design-build structures are all supported without fighting the tool.",
    },
    {
      accentColor: "#f687b3",
      icon: (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <rect x="2" y="13" width="3" height="5" rx="1" fill="#f687b3"/>
          <rect x="7" y="9" width="3" height="9" rx="1" fill="#f687b3" opacity=".7"/>
          <rect x="12" y="5" width="3" height="13" rx="1" fill="#f687b3" opacity=".5"/>
          <path d="M3.5 12 Q7 7 10.5 8.5 Q14 10 17 5" stroke="#f687b3" strokeWidth="1.2" fill="none" strokeLinecap="round" strokeDasharray="2 1.5" opacity=".7"/>
        </svg>
      ),
      title: "Historical Intelligence",
      desc: "Capture project benchmarks and lessons learned as you go. Draw on historical cost data when starting new estimates — so every project makes you sharper than the last.",
    },
    {
      accentColor: "#63b3ed",
      icon: (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <circle cx="7" cy="8" r="3" stroke="#63b3ed" strokeWidth="1.5" fill="none"/>
          <circle cx="14" cy="5" r="2.5" stroke="#63b3ed" strokeWidth="1.4" fill="none" opacity=".7"/>
          <path d="M1 16c0-2.5 2-4 6-4s6 1.5 6 4" stroke="#63b3ed" strokeWidth="1.5" strokeLinecap="round" fill="none"/>
          <path d="M14 10c2.5.5 4 1.8 4 3.5" stroke="#63b3ed" strokeWidth="1.3" strokeLinecap="round" fill="none" opacity=".6"/>
          <circle cx="14" cy="14" r="1.2" fill="#63b3ed" opacity=".5"/>
        </svg>
      ),
      title: "Real-Time Collaboration",
      desc: "Multiple estimators can work the same project simultaneously. See live cursors, instant edits, and role-based access — Owner, Admin, Editor, or Viewer — so everyone sees exactly what they should.",
    },
    {
      accentColor: "#68d391",
      icon: (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <rect x="2" y="3" width="16" height="14" rx="2" stroke="#68d391" strokeWidth="1.5" fill="none"/>
          <line x1="2" y1="8" x2="18" y2="8" stroke="#68d391" strokeWidth="1" opacity=".4"/>
          <line x1="8" y1="3" x2="8" y2="17" stroke="#68d391" strokeWidth="1" opacity=".4"/>
          <line x1="8" y1="8" x2="18" y2="8" stroke="#68d391" strokeWidth="1" opacity=".4"/>
          <rect x="9" y="9" width="4" height="3" rx=".5" fill="#68d391" opacity=".3"/>
          <rect x="14" y="9" width="3" height="3" rx=".5" fill="#68d391" opacity=".5"/>
        </svg>
      ),
      title: "Conceptual Budget Module",
      desc: "Start before the drawings exist. Set system-level targets, assign cost-per-SF benchmarks, and track design-to-budget alignment from day one of schematic design.",
    },
  ];

  return (
    <div style={{ background: BG, minHeight: "100vh", fontFamily: FONT, color: TEXT }}>
      {/* ── Sticky Nav ─────────────────────────────────────────────────────── */}
      <nav style={{
        position: "sticky", top: 0, zIndex: 100,
        background: scrolled ? BG + "f2" : BG,
        backdropFilter: scrolled ? "blur(12px)" : "none",
        borderBottom: `1px solid ${scrolled ? BORDER : "transparent"}`,
        padding: `0 ${hPad}`,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        height: 64,
        transition: "border-color .2s, background .2s",
      }}>
        {/* Logo + wordmark */}
        <OEWordmark
          size={26}
          wordColor={TEXT}
          accentColor={ACCENT}
          subColor={TEXT2}
          circleColor="rgba(255,255,255,0.4)"
          markBarColor="rgba(79,209,197,0.4)"
        />
        {/* Nav actions */}
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <button onClick={onLogin} style={btn(false, true)} onMouseOver={e=>(e.currentTarget.style.borderColor=ACCENT)} onMouseOut={e=>(e.currentTarget.style.borderColor=BORDER)}>
            Log In
          </button>
          {!isMobile && (
            <button onClick={openModal} style={btn(true, true)} onMouseOver={e=>(e.currentTarget.style.opacity=".85")} onMouseOut={e=>(e.currentTarget.style.opacity="1")}>
              Request Early Access
            </button>
          )}
        </div>
      </nav>

      {/* ── Hero ────────────────────────────────────────────────────────────── */}
      <section style={{
        minHeight: isTablet ? "auto" : "95vh", display: "flex", alignItems: "center",
        justifyContent: "center",
        padding: isMobile ? "60px 20px 48px" : isTablet ? "70px 32px 60px" : "80px 48px 60px",
      }}>
        <div style={{ maxWidth: 1160, width: "100%", display: "flex", alignItems: isTablet ? "flex-start" : "center", flexDirection: isTablet ? "column" : "row", gap: isTablet ? 36 : 52 }}>
        {/* Left: copy */}
        <div style={{ flex: isTablet ? "none" : "0 0 400px", maxWidth: isTablet ? "100%" : 400, width: "100%" }}>
          {/* Category badge */}
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 8,
            fontSize: 11, fontWeight: 700, letterSpacing: ".1em",
            color: ACCENT, textTransform: "uppercase",
            background: ACCENT + "12", border: `1px solid ${ACCENT}30`,
            borderRadius: 20, padding: "5px 14px", marginBottom: 28,
          }}>
            Pre-Construction Estimating Platform
          </div>
          <h1 style={{
            margin: "0 0 20px", lineHeight: 1.08,
            fontSize: isMobile ? "clamp(28px, 9vw, 38px)" : "clamp(36px, 4vw, 56px)",
            fontWeight: 900, letterSpacing: "-.035em", color: TEXT,
          }}>
            Pre-construction<br/>
            for builders who<br/>
            <span style={{ color: ACCENT }}>set the standard.</span>
          </h1>
          <p style={{
            fontSize: 16, lineHeight: 1.75, color: TEXT2,
            margin: "0 0 32px", maxWidth: 390,
          }}>
            From the first order-of-magnitude to a signed contract — track cost confidence at every line, structure alternates clearly, and deliver reports that hold up through every design iteration.
          </p>
          <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 16 }}>
            <button onClick={openModal} style={btn(true)}
              onMouseOver={e=>(e.currentTarget.style.opacity=".85")} onMouseOut={e=>(e.currentTarget.style.opacity="1")}>
              Request Early Access →
            </button>
            <button onClick={onLogin} style={btn(false)}
              onMouseOver={e=>(e.currentTarget.style.borderColor=ACCENT)} onMouseOut={e=>(e.currentTarget.style.borderColor=BORDER)}>
              Log In
            </button>
          </div>
          {/* Differentiator pills */}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {[
              { dot: "#68d391", label: "Cost Confidence Scoring" },
              { dot: ACCENT2,   label: "Value Engineering Tools" },
              { dot: ACCENT,    label: "Client-Ready Reports"    },
              { dot: "#b794f4", label: "AI-Powered Import"       },
            ].map(p => (
              <div key={p.label} style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                padding: "5px 12px", borderRadius: 20,
                background: BG3, border: `1px solid ${BORDER}`,
                fontSize: 11, color: TEXT2, fontFamily: FONT,
              }}>
                <span style={{ width: 5, height: 5, borderRadius: "50%", background: p.dot, display: "inline-block", flexShrink: 0 }}/>
                {p.label}
              </div>
            ))}
          </div>
        </div>

        {/* Right: dashboard panels */}
        <div style={{ flex: 1, minWidth: 0, maxWidth: isTablet ? "100%" : 640, width: "100%", display: "flex", flexDirection: "column", gap: 14 }}>
          {/* Top: EstimatePanel with floating badge */}
          <div style={{ position: "relative", filter: "drop-shadow(0 24px 60px rgba(79,209,197,.18))" }}>
            <EstimatePanel/>
            {/* Floating badge overlay */}
            <div style={{
              position: "absolute", top: -14, right: 20,
              background: BG2, border: `1px solid ${ACCENT}55`,
              borderRadius: 20, padding: "5px 13px",
              fontSize: 11, fontWeight: 700, color: ACCENT,
              fontFamily: FONT, letterSpacing: ".04em",
              display: "flex", alignItems: "center", gap: 6,
              boxShadow: `0 4px 16px rgba(79,209,197,.18)`,
            }}>
              <span style={{ width: 7, height: 7, borderRadius: "50%", background: ACCENT, display: "inline-block" }}/>
              Contractor Bid Linking
            </div>
          </div>

          {/* Bottom row: AlternatesPanel + AnalyticsPanel */}
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 14 }}>
            {/* Alternates cell */}
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 7 }}>
                <span style={{
                  fontSize: 9, fontWeight: 700, letterSpacing: ".1em",
                  color: "#68d391", background: "#68d391" + "18",
                  border: `1px solid #68d39144`, borderRadius: 20,
                  padding: "3px 9px",
                }}>VALUE ENGINEERING</span>
                <span style={{ fontSize: 11, color: TEXT2, fontFamily: FONT }}>Add &amp; deductive VE scenarios</span>
              </div>
              <div style={{ filter: "drop-shadow(0 12px 32px rgba(0,0,0,.4))" }}>
                <VEPanel/>
              </div>
            </div>

            {/* Analytics cell */}
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 7 }}>
                <span style={{
                  fontSize: 9, fontWeight: 700, letterSpacing: ".1em",
                  color: ACCENT, background: ACCENT + "18",
                  border: `1px solid ${ACCENT}44`, borderRadius: 20,
                  padding: "3px 9px",
                }}>BID TIMELINE</span>
                <span style={{ fontSize: 11, color: TEXT2, fontFamily: FONT }}>Live bid tracking &amp; leveling</span>
              </div>
              <div style={{ filter: "drop-shadow(0 12px 32px rgba(0,0,0,.4))" }}>
                <BidTimelinePanel/>
              </div>
            </div>
          </div>
        </div>
        </div>
      </section>

      {/* ── The Problem ─────────────────────────────────────────────────────── */}
      <section style={{ background: BG2, borderTop: `1px solid ${BORDER}`, padding: `${isMobile ? 56 : 88}px ${hPad}` }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 56 }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".14em", color: ACCENT2, textTransform: "uppercase", marginBottom: 14 }}>
              Why OpenEstimate Exists
            </div>
            <h2 style={{ margin: 0, fontSize: "clamp(24px,3.5vw,38px)", fontWeight: 900, letterSpacing: "-.025em", color: TEXT, lineHeight: 1.15 }}>
              Say goodbye to<br/>your old spreadsheets.
            </h2>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3,1fr)", gap: 24 }}>
            {[
              {
                n: "01",
                color: "#fc8181",
                title: "Your estimate lives in a spreadsheet no owner wants to look at.",
                body: "Rows of numbers mean nothing to clients. What they need is a clear, visual narrative — scope organized by proposal, alternates isolated, and confidence shown explicitly.",
              },
              {
                n: "02",
                color: ACCENT2,
                title: "You can't tell anyone — including yourself — how confident you are.",
                body: "Hard bids, allowances, and uncertain line items all look the same on a spreadsheet. When the project goes over budget, nobody saw it coming. But you could have.",
              },
              {
                n: "03",
                color: ACCENT,
                title: "Every design decision has a cost — and you should know it the moment it's made.",
                body: "Track alternates, pricing milestones, and scope changes in real time. When the design evolves, your estimate evolves with it — so every decision is made with full cost clarity.",
              },
            ].map(item => (
              <div key={item.n} style={{
                background: BG3, border: `1px solid ${BORDER}`,
                borderRadius: 10, padding: "32px 28px",
                display: "flex", flexDirection: "column", gap: 16,
              }}>
                <div style={{ fontSize: 32, fontWeight: 900, color: item.color, opacity: .35, letterSpacing: "-.04em", lineHeight: 1 }}>{item.n}</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: TEXT, lineHeight: 1.4, letterSpacing: "-.01em" }}>{item.title}</div>
                <div style={{ fontSize: 13, color: TEXT2, lineHeight: 1.7 }}>{item.body}</div>
              </div>
            ))}
          </div>
          <div style={{ textAlign: "center", marginTop: 52, borderTop: `1px solid ${BORDER}`, paddingTop: 48 }}>
            <p style={{ fontSize: 18, fontWeight: 600, color: TEXT, lineHeight: 1.6, maxWidth: 640, margin: "0 auto 8px", letterSpacing: "-.01em" }}>
              OpenEstimate is built for the entire pre-construction arc —
              from the first order-of-magnitude through contract execution.
            </p>
            <p style={{ fontSize: 14, color: TEXT2, margin: 0, lineHeight: 1.7, maxWidth: 560, marginLeft: "auto", marginRight: "auto" }}>
              Structured for how estimating actually works: iterative, multi-party, and never quite finished until it is.
            </p>
          </div>
        </div>
      </section>

      {/* ── Features ────────────────────────────────────────────────────────── */}
      <section style={{ background: BG, padding: `${isMobile ? 56 : 80}px ${hPad}` }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 52 }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".14em", color: ACCENT, textTransform: "uppercase", marginBottom: 14 }}>
              Precision Without Compromise
            </div>
            <h2 style={{ margin: 0, fontSize: "clamp(26px,3.5vw,40px)", fontWeight: 900, letterSpacing: "-.025em", color: TEXT, lineHeight: 1.15 }}>
              Built for how pre-construction<br/>actually works.
            </h2>
          </div>
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
            gap: 20,
          }}>
            {features.map(f => <FeatureCard key={f.title} {...f}/>)}
          </div>
        </div>
      </section>

      {/* ── How It Works ────────────────────────────────────────────────────── */}
      <section style={{ padding: `${isMobile ? 56 : 80}px ${hPad}` }}>
        <div style={{ maxWidth: 1000, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 56 }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".14em", color: ACCENT, textTransform: "uppercase", marginBottom: 14 }}>
              The Workflow
            </div>
            <h2 style={{ margin: 0, fontSize: "clamp(24px,3vw,36px)", fontWeight: 900, letterSpacing: "-.025em", color: TEXT, lineHeight: 1.15 }}>
              From first number to contract execution
            </h2>
          </div>
          <div style={{ display: "flex", gap: isMobile ? 24 : 32, flexDirection: isMobile ? "column" : "row" }}>
            <Step n={1} title="Start with a conceptual budget" stacked={isMobile}
              desc="Begin at schematic design with a system-level budget. Set benchmarks, areas, and cost targets before a single line item exists."/>
            <Step n={2} title="Develop the detailed estimate" stacked={isMobile}
              desc="Build CSI MasterFormat line items as design progresses. Tag each line with confidence status, trade partner, and proposal grouping."/>
            <Step n={3} title="Present, refine, and close" last stacked={isMobile}
              desc="Present client-ready reports at every milestone. Compare snapshots as the design evolves. Lock your estimate when the scope is right."/>
          </div>
        </div>
      </section>


      {/* ── Early Access CTA ────────────────────────────────────────────────── */}
      <section style={{
        background: BG2,
        borderTop: `1px solid ${BORDER}`,
        borderBottom: `1px solid ${BORDER}`,
        padding: `${isMobile ? 60 : 88}px ${hPad}`,
        textAlign: "center",
      }}>
        <div style={{ maxWidth: 580, margin: "0 auto" }}>
          {/* Accent glow dot */}
          <div style={{
            width: 10, height: 10, borderRadius: "50%",
            background: ACCENT, margin: "0 auto 28px",
            boxShadow: `0 0 24px 6px ${ACCENT}55`,
          }}/>
          <h2 style={{
            margin: "0 0 16px", fontSize: "clamp(26px,4vw,42px)",
            fontWeight: 900, letterSpacing: "-.03em", color: TEXT, lineHeight: 1.15,
          }}>
            Raise the standard<br/>on your next project.
          </h2>
          <p style={{ fontSize: 16, color: TEXT2, lineHeight: 1.75, margin: "0 0 40px" }}>
            OpenEstimate is in invite-only beta. We're onboarding builders and developers who care deeply about the pre-construction process. If that's you, request access below.
          </p>
          <button onClick={openModal} style={{
            ...btn(true),
            fontSize: 16, padding: "15px 40px",
            boxShadow: `0 0 32px 0 ${ACCENT}30`,
            width: isMobile ? "100%" : undefined,
            justifyContent: isMobile ? "center" : undefined,
          }}
            onMouseOver={e=>(e.currentTarget.style.opacity=".85")} onMouseOut={e=>(e.currentTarget.style.opacity="1")}>
            Request Early Access →
          </button>
          <div style={{ marginTop: 22, fontSize: 13, color: TEXT2 }}>
            Already have an account?{" "}
            <button onClick={onLogin} style={{
              background: "none", border: "none", color: ACCENT,
              cursor: "pointer", fontFamily: FONT, fontSize: 13, fontWeight: 600,
              textDecoration: "underline", textUnderlineOffset: 3,
            }}>
              Log in →
            </button>
          </div>
        </div>
      </section>

      {/* ── Contact + Footer ─────────────────────────────────────────────── */}
      <ContactFooter hPad={hPad} isMobile={isMobile} />

      {/* ── Request Early Access Modal ──────────────────────────────────── */}
      {showModal && (
        <div onClick={()=>setShowModal(false)} style={{
          position:"fixed",inset:0,background:"rgba(0,0,0,.7)",backdropFilter:"blur(4px)",
          display:"flex",alignItems:"center",justifyContent:"center",zIndex:9999,padding:20,
        }}>
          <div onClick={e=>e.stopPropagation()} style={{
            background:BG2,border:`1px solid ${BORDER}`,borderRadius:10,
            padding:"36px 40px",width:"100%",maxWidth:460,boxShadow:"0 24px 80px rgba(0,0,0,.6)",
          }}>
            {reqState === "done" ? (
              /* ── Success state ── */
              <div style={{textAlign:"center",padding:"8px 0"}}>
                <div style={{marginBottom:16,display:"flex",justifyContent:"center"}}><Icon.CheckCircle size={20}/></div>
                <h3 style={{margin:"0 0 12px",fontSize:22,fontWeight:800,color:TEXT}}>Request received!</h3>
                <p style={{fontSize:14,color:TEXT2,lineHeight:1.7,margin:"0 0 28px"}}>
                  We'll review your request and send you an email at <strong style={{color:TEXT}}>{reqEmail}</strong> when access is granted.
                </p>
                <button onClick={()=>setShowModal(false)} style={{...btn(true,true),width:"100%",justifyContent:"center"}}>
                  Close
                </button>
              </div>
            ) : (
              /* ── Form state ── */
              <>
                <div style={{marginBottom:28}}>
                  <h3 style={{margin:"0 0 6px",fontSize:22,fontWeight:800,color:TEXT}}>Request Early Access</h3>
                  <p style={{margin:0,fontSize:13,color:TEXT2,lineHeight:1.6}}>
                    OpenEstimate is currently in private beta. Submit your request and we'll reach out when your access is approved.
                  </p>
                </div>

                {[
                  {label:"Your name",placeholder:"Jane Smith",value:reqName,setter:setReqName,type:"text"},
                  {label:"Email address",placeholder:"jane@company.com",value:reqEmail,setter:setReqEmail,type:"email"},
                ].map(({label,placeholder,value,setter,type})=>(
                  <div key={label} style={{marginBottom:16}}>
                    <label style={{display:"block",fontSize:11,fontWeight:600,color:TEXT2,marginBottom:6,letterSpacing:".05em",textTransform:"uppercase"as const}}>
                      {label} <span style={{color:ACCENT}}>*</span>
                    </label>
                    <input
                      type={type}
                      placeholder={placeholder}
                      value={value}
                      onChange={e=>setter(e.target.value)}
                      style={{width:"100%",background:BG3,border:`1px solid ${BORDER}`,borderRadius:6,
                        padding:"10px 14px",fontSize:14,color:TEXT,fontFamily:FONT,outline:"none",
                        boxSizing:"border-box"as const}}
                      onFocus={e=>e.target.style.borderColor=ACCENT}
                      onBlur={e=>e.target.style.borderColor=BORDER}
                    />
                  </div>
                ))}

                <div style={{marginBottom:24}}>
                  <label style={{display:"block",fontSize:11,fontWeight:600,color:TEXT2,marginBottom:6,letterSpacing:".05em",textTransform:"uppercase"as const}}>
                    What are you building? <span style={{color:TEXT2,fontWeight:400}}>(optional)</span>
                  </label>
                  <textarea
                    placeholder="Tell us a bit about your work or company…"
                    value={reqMsg}
                    onChange={e=>setReqMsg(e.target.value)}
                    rows={3}
                    style={{width:"100%",background:BG3,border:`1px solid ${BORDER}`,borderRadius:6,
                      padding:"10px 14px",fontSize:14,color:TEXT,fontFamily:FONT,outline:"none",
                      resize:"vertical"as const,boxSizing:"border-box"as const}}
                    onFocus={e=>e.target.style.borderColor=ACCENT}
                    onBlur={e=>e.target.style.borderColor=BORDER}
                  />
                </div>

                {reqState === "error" && (
                  <div style={{marginBottom:16,padding:"10px 14px",background:"#c0392b22",border:"1px solid #c0392b44",borderRadius:6,fontSize:13,color:"#e74c3c"}}>
                    Something went wrong. Please try again or email us directly.
                  </div>
                )}

                <div style={{display:"flex",gap:10}}>
                  <button onClick={()=>setShowModal(false)}
                    style={{flex:1,padding:"11px 0",background:"none",border:`1.5px solid ${BORDER}`,borderRadius:6,
                      color:TEXT2,fontFamily:FONT,fontWeight:600,fontSize:14,cursor:"pointer"}}>
                    Cancel
                  </button>
                  <button onClick={submitRequest}
                    disabled={reqState==="sending"||!reqName.trim()||!reqEmail.trim()}
                    style={{flex:2,padding:"11px 0",background:ACCENT,border:"none",borderRadius:6,
                      color:BG,fontFamily:FONT,fontWeight:700,fontSize:14,cursor:"pointer",
                      opacity:(reqState==="sending"||!reqName.trim()||!reqEmail.trim())?0.5:1,
                      transition:"opacity .15s"}}>
                    {reqState === "sending" ? "Sending…" : "Submit Request →"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
